import { useEffect, useMemo, useRef, useState } from "react";
import { Send, Loader2, Mail, Eye, StickyNote, AlertTriangle, CornerUpLeft } from "lucide-react";
import { Avatar, Button, Input, Modal, Skeleton, Textarea, useToast } from "@/components/ui";
import { MAX_SMS_PARTS, describeForced, smsLength, tidyForSms } from "@/lib/sms-length";
import { ChannelIcon, getContactChannels } from "./ChannelPicker";
import { api, ApiError } from "@/lib/api";
import { channelLabel, formatRelative } from "@/lib/format";
import { formatSender, syncsuiteEmailSettingsUrl, useEmailSending, type EmailSendingConfig } from "@/lib/email-sending";
import type { Contact, Conversation, Message } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Unified chat: every contact has exactly one thread. Inbound messages
 * from any channel (SMS, email, IG DM, WhatsApp, etc.) interleave
 * chronologically. The composer has a channel chip row that decides
 * what provider gets the next outbound — the conversation itself is
 * channel-agnostic.
 *
 * `conversationId` is null when the contact has no thread yet; the
 * first send creates it server-side and `onCreated` flips the parent
 * into "have a thread" mode.
 */
export function ConversationView({
  contact,
  conversationId,
  onCreated,
  onAfterSend,
  emptyMessage,
}: {
  contact: Contact;
  conversationId: string | null;
  onCreated?: (conversationId: string) => void;
  onAfterSend?: () => void;
  emptyMessage?: string;
}) {
  const toast = useToast();
  const [active, setActive] = useState<{ conversation: Conversation; messages: Message[] } | null>(null);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  // The thread scrolls in a column-reverse box, so it opens on the newest
  // message and stays pinned there while email previews and images grow
  // after first paint. scrollTop 0 = the bottom in that layout.
  const scrollRef = useRef<HTMLDivElement>(null);
  const toBottom = (behavior: ScrollBehavior = "auto") => scrollRef.current?.scrollTo({ top: 0, behavior });
  // Ready to type when a thread opens, without scrolling the page down to the
  // box (a stacked layout would jump past the person's details) and without
  // popping the keyboard open on phones.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const readyKey = active ? active.conversation?.id ?? "draft" : conversationId ? "loading" : "draft";
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia("(min-width: 768px)").matches) return;
    const t = window.setTimeout(() => composerRef.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(t);
    // Re-run once the thread has loaded: the box only exists after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, contact.id, readyKey]);

  // Channels we can reach the contact on. Internal-note is always
  // available regardless of identities. The selected channel persists
  // per contact via localStorage so the next message defaults to
  // whatever the user picked last time.
  const readyChannels = useMemo(() => getContactChannels(contact).filter((c) => c.ready), [contact]);

  const initialChannel = useMemo(() => {
    const stored = typeof window !== "undefined"
      ? window.localStorage.getItem(`crm.contact.${contact.id}.lastChannel`)
      : null;
    if (stored && readyChannels.some((c) => c.channel === stored)) return stored;
    // Default to whatever the latest message used, falling back to the
    // first reachable identity. Skips internal_note as a default.
    const realChannels = readyChannels.filter((c) => c.channel !== "internal_note");
    return realChannels[0]?.channel ?? readyChannels[0]?.channel ?? "internal_note";
  }, [contact.id, readyChannels]);

  const [composerChannel, setComposerChannel] = useState<string>(initialChannel);
  useEffect(() => { setComposerChannel(initialChannel); }, [initialChannel]);

  // Sending identity for the From line above the composer. Only fetched
  // once the user is actually on the email channel — no reason to hit the
  // endpoint for an SMS-only contact.
  const { config: sendingConfig } = useEmailSending(composerChannel === "email");

  // Load + poll the active conversation.
  //
  // Identity gates throughout: a response is only adopted if its
  // conversation.id matches the prop AND the contact_id matches our
  // contact.id. This is the defensive layer against the "wrong messages
  // briefly showing under the wrong contact" class of bug — even a
  // misrouted backend response or a slow-resolving stale fetch can't
  // leak another conversation's content into the UI.
  // Toast accessed via ref so this effect's deps don't include a context
  // value (defensive — if any context provider above us forgets to
  // memoize its value, our chat shouldn't reload on every render).
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // The conversation + contact this component is CURRENTLY showing. Async
  // work started before a switch reads these on completion instead of the
  // values it captured when it started, so a reply that lands after the
  // user has moved on can't paint itself into the new thread. The load and
  // poll effects have their own `cancelled` flags; this covers `send`,
  // which outlives its effect.
  const viewingRef = useRef({ conversationId, contactId: contact.id });
  viewingRef.current = { conversationId, contactId: contact.id };
  /** Did the view stay put? Pass the ids captured when the work started. */
  const isStillViewing = (convId: string | null, contactId: string) =>
    viewingRef.current.conversationId === convId &&
    viewingRef.current.contactId === contactId;

  useEffect(() => {
    setActive(null);
    setBody("");
    setSubject("");
    // A send in flight for the previous thread no longer owns this
    // composer — re-enable it immediately rather than waiting on a
    // request that will be gated out anyway.
    setSending(false);
    if (!conversationId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{ conversation: Conversation; messages: Message[] }>(`/conversations/${conversationId}`);
        if (cancelled) return;
        if (data.conversation.id !== conversationId) return;
        if (data.conversation.contact_id !== contact.id) return;
        // Drop any message whose conversation_id doesn't match — server
        // shouldn't return any, but if it ever does we won't render it.
        const safeMessages = data.messages.filter(
          (m) => m.conversation_id === conversationId
        );
        setActive({ conversation: data.conversation, messages: safeMessages });
        setTimeout(() => toBottom(), 0);
        if (data.conversation.unread_count > 0) {
          void api.patch(`/conversations/${conversationId}`, { mark_read: true });
        }
      } catch (err) {
        if (!cancelled) toastRef.current.error(err instanceof ApiError ? err.message : "Failed to load conversation");
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, contact.id]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.hidden) return;
      try {
        const data = await api.get<{ conversation: Conversation; messages: Message[] }>(`/conversations/${conversationId}`);
        if (cancelled) return;
        if (data.conversation.id !== conversationId) return;
        if (data.conversation.contact_id !== contact.id) return;
        const safeMessages = data.messages.filter(
          (m) => m.conversation_id === conversationId
        );
        setActive((prev) => {
          if (!prev) return { conversation: data.conversation, messages: safeMessages };
          // Merge polled data into the existing state by preserving
          // object references for unchanged rows. React's reconciliation
          // skips re-rendering a child when its props are
          // referentially-equal to the previous render — so message
          // bubbles, avatars, and the right rail's chips/timeline don't
          // flash on every 5-second tick. The ONLY rows that get a new
          // reference are the ones whose content actually changed (a
          // queued message transitioning to delivered, a new inbound
          // arriving, etc).
          const prevById = new Map(prev.messages.map((m) => [m.id, m]));
          let changed = prev.messages.length !== safeMessages.length;
          const mergedMessages = safeMessages.map((m, i) => {
            const old = prevById.get(m.id);
            if (!old) { changed = true; return m; }
            // Same id → check if any displayed field actually moved.
            const samePos = prev.messages[i]?.id === m.id;
            const sameStatus = old.status === m.status;
            const sameBody = old.body === m.body;
            const sameDelivered = old.delivered_at === m.delivered_at;
            const sameRead = old.read_at === m.read_at;
            const sameError = old.error === m.error;
            if (samePos && sameStatus && sameBody && sameDelivered && sameRead && sameError) {
              return old; // identical → keep old reference, no re-render
            }
            changed = true;
            return m;
          });
          // Optimistic-message reconciliation: any tmp_* row in prev
          // that now has a real server twin (matched by body+timestamp
          // proximity is fragile, so just keep tmp rows until the next
          // poll picks up their server-issued id naturally).
          const sameUnread = prev.conversation.unread_count === data.conversation.unread_count;
          if (!changed && sameUnread) return prev;
          return {
            // Only mint a new conversation reference if its displayed
            // bits moved — keeps subject/header stable across polls.
            conversation: sameUnread &&
              prev.conversation.subject === data.conversation.subject &&
              prev.conversation.status === data.conversation.status
              ? prev.conversation
              : data.conversation,
            messages: changed ? mergedMessages : prev.messages,
          };
        });
      } catch { /* transient */ }
    };
    const timer = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [conversationId, contact.id]);

  const send = async () => {
    if (!body.trim()) return;
    const sentBody = body;
    const sentSubject = subject;
    const channel = composerChannel;

    // Persist the user's last channel choice so the next outbound
    // for this contact defaults to it.
    try { window.localStorage.setItem(`crm.contact.${contact.id}.lastChannel`, channel); } catch { /* ok */ }

    // Ids this send belongs to. Everything below is gated on the view
    // still showing them — switching threads mid-send must not splice this
    // thread's result into another one.
    const sentConversationId = conversationId;
    const sentContactId = contact.id;

    if (sentConversationId && active) {
      // Append to the existing thread.
      setSending(true);
      const optimistic: Message = {
        id: `tmp_${Date.now()}`,
        conversation_id: sentConversationId,
        contact_id: active.conversation.contact_id,
        direction: "outbound",
        channel,
        status: "queued",
        body: sentBody,
        subject: sentSubject || null,
        attachments: [], external_id: null,
        author_type: "user", author_id: null,
        from_address: null, from_name: null,
        error: null,
        sent_at: null, delivered_at: null, read_at: null,
        created_at: new Date().toISOString(),
      };
      // Functional update — `active` from the closure can already be a
      // render behind if a poll tick landed between keystroke and click.
      setActive((prev) => (prev ? { ...prev, messages: [...prev.messages, optimistic] } : prev));
      setBody(""); setSubject("");
      setTimeout(() => toBottom("smooth"), 0);
      try {
        await api.post(`/conversations/${sentConversationId}/messages`, {
          channel,
          body: sentBody,
          subject: sentSubject || undefined,
        });
        const data = await api.get<{ conversation: Conversation; messages: Message[] }>(`/conversations/${sentConversationId}`);
        // Same identity gates the load/poll paths use, plus the "are we
        // still here" check — the send survives unmount of the effects.
        if (!isStillViewing(sentConversationId, sentContactId)) return;
        if (data.conversation.id !== sentConversationId) return;
        if (data.conversation.contact_id !== sentContactId) return;
        setActive({
          conversation: data.conversation,
          messages: data.messages.filter((m) => m.conversation_id === sentConversationId),
        });
        onAfterSend?.();
      } catch (err) {
        // A failure still belongs to the thread it was typed in — only
        // surface it (and mark the bubble) if that thread is on screen.
        if (!isStillViewing(sentConversationId, sentContactId)) return;
        toast.error(err instanceof ApiError ? err.message : "Send failed");
        setActive((prev) => prev ? { ...prev, messages: prev.messages.map(m => m.id === optimistic.id ? { ...m, status: "failed", error: "Send failed" } : m) } : prev);
      } finally {
        // Always clear — `sending` is a composer-local flag, and leaving
        // it set after a thread switch would disable the new composer.
        setSending(false);
      }
      return;
    }

    // No thread yet — create it via the upsert route.
    setSending(true);
    setBody(""); setSubject("");
    try {
      const r = await api.post<{ conversation: { id: string }; error: string | null }>("/conversations", {
        contact_id: sentContactId,
        channel,
        subject: sentSubject || undefined,
        body: sentBody,
      });
      // The thread was created for THIS contact — if the user has since
      // moved to another one, don't hand the parent a conversation id
      // belonging to a contact it's no longer showing.
      if (!isStillViewing(null, sentContactId)) return;
      if (r.error) toast.error(r.error);
      onCreated?.(r.conversation.id);
      onAfterSend?.();
    } catch (err) {
      if (!isStillViewing(null, sentContactId)) return;
      toast.error(err instanceof ApiError ? err.message : "Send failed");
      setBody(sentBody); setSubject(sentSubject);
    } finally {
      setSending(false);
    }
  };

  if (!conversationId && readyChannels.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-center p-8">
        <p className="text-sm text-zinc-500">{emptyMessage ?? "Pick a conversation to read or reply"}</p>
      </div>
    );
  }

  if (conversationId && !active) {
    return <MessagesSkeleton />;
  }

  const isInternalNote = composerChannel === "internal_note";
  const channelName = channelLabel(composerChannel);

  return (
    <>
      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col-reverse overflow-y-auto p-4">
        <div className="flex min-h-full flex-col justify-end space-y-3">
          {!active || active.messages.length === 0 ? (
            <p className="m-auto py-12 text-center text-sm text-zinc-500">
              {conversationId ? "No messages yet. Say hi 👋" : `Start a conversation. The first message goes by ${channelName}.`}
            </p>
          ) : active.messages.map((m) => (
            <MessageBubble key={m.id} message={m} contactName={contact.first_name ?? contact.last_name ?? null} contactAvatar={contact.avatar_url} />
          ))}
        </div>
      </div>

      <div className={cn(
        "border-t p-3 shrink-0 transition-colors",
        isInternalNote ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800/70 bg-zinc-950",
      )}>
        {/* Channel picker — every reachable channel for this contact,
            plus internal_note. Selecting just changes what provider the
            next message routes to; the thread itself is channel-agnostic. */}
        {readyChannels.length > 1 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {readyChannels.map((c) => (
              <button
                key={c.channel}
                type="button"
                onClick={() => setComposerChannel(c.channel)}
                title={c.hint}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2 h-7 text-xs font-medium transition-colors",
                  composerChannel === c.channel
                    ? c.channel === "internal_note"
                      ? "bg-amber-500/30 text-amber-100"
                      : "bg-indigo-500 text-white"
                    : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700",
                )}
              >
                <c.icon className="h-3 w-3" />
                {c.label}
              </button>
            ))}
          </div>
        )}
        {composerChannel === "email" && (
          <>
            <ComposerSenderLine config={sendingConfig} />
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={active?.conversation.subject ? `Re: ${active.conversation.subject}` : "Subject"}
              className="h-8 text-xs mb-2"
            />
          </>
        )}
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={isInternalNote
            ? "Internal note: only your team sees it"
            : `Message via ${channelName}…`}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send(); }}
          className={cn("min-h-12", isInternalNote && "bg-amber-500/5")}
          ref={composerRef}
        />
        {/* What this text will actually cost, before it is sent. */}
        {composerChannel === "sms" && body.trim() !== "" && <SmsMeter body={body} />}
        <div className="mt-2 flex items-center justify-between">
          <span className={cn(
            "text-[10px] inline-flex items-center gap-1",
            isInternalNote ? "text-amber-400" : "text-zinc-500",
          )}>
            <ChannelIcon channel={composerChannel} /> {channelName} · ⌘+Enter to {isInternalNote ? "add" : "send"}
          </span>
          <Button
            onClick={send}
            loading={sending}
            disabled={!body.trim() || (composerChannel === "sms" && smsLength(tidyForSms(body)).tooLong)}
            size="sm"
            variant={isInternalNote ? "outline" : "primary"}
            leftIcon={isInternalNote ? <StickyNote className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
          >
            {isInternalNote ? "Add note" : "Send"}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * "From: …" line above the email composer. The whole point of this
 * feature: nobody should have to send an email to discover what address
 * it went out as. Three states —
 *
 *   - sendable  → the exact From (and Reply-To, when it differs)
 *   - blocked   → no verified domain, or no mailing address on file in
 *                 SyncSuite, with the fix one click away
 *   - unknown   → config fetch failed; stay silent rather than guess
 *
 * The address shown comes from the same resolver the send uses, so this
 * can't drift from reality.
 */
function ComposerSenderLine({ config }: { config: EmailSendingConfig | null }) {
  // `known: false` = the platform lookup failed. Show nothing rather than
  // claim a setup problem the user doesn't have.
  if (!config || !config.known) return null;

  const settingsUrl = syncsuiteEmailSettingsUrl();

  if (!config.can_send) {
    // Two different blockers, two different fixes — say which one it is
    // rather than sending someone to re-check a domain that's already
    // verified. A missing domain is the more fundamental of the two, so
    // it wins when both are missing.
    const noDomain = config.verified_domains.length === 0;
    return (
      <div className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-200">
        <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
        <span>
          {noDomain
            ? "No verified sending domain yet, so emails will fail. "
            : "No business mailing address on file, so emails will fail. It isn't shown in personal email, but it's required before anything sends. "}
          {settingsUrl ? (
            <a href={settingsUrl} target="_blank" rel="noreferrer" className="font-medium underline hover:text-amber-100">
              {noDomain ? "Verify a domain in SyncSuite" : "Add your address in SyncSuite"}
            </a>
          ) : (
            <span className="font-medium">
              {noDomain
                ? "Verify one in SyncSuite → Integrations → Email."
                : "Add it in SyncSuite → Integrations → Email."}
            </span>
          )}
        </span>
      </div>
    );
  }

  const from = formatSender(config.effective.from_name, config.effective.from);
  if (!from) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5 text-[11px]">
      <span className="font-medium uppercase tracking-wider text-zinc-500">From</span>
      <span className="text-zinc-200">{from}</span>
      {config.effective.reply_to && (
        <span className="inline-flex items-center gap-1 text-zinc-500">
          <CornerUpLeft className="h-3 w-3" />
          Replies go to {config.effective.reply_to}
        </span>
      )}
      {config.configured_domain_verified === false && (
        <span className="inline-flex items-center gap-1 text-amber-400">
          <AlertTriangle className="h-3 w-3" />
          {config.configured_domain} isn't verified, so this send will fail
        </span>
      )}
    </div>
  );
}

/**
 * Skeleton row pattern for an unloaded chat thread. Alternates inbound /
 * outbound bubbles with realistic widths so the layout doesn't pop when
 * real messages slot in. Used both here (while messages fetch) and from
 * the inbox page (while the contact behind a freshly-clicked
 * conversation resolves) so the visual loading state is identical
 * across both transitions.
 */
export function MessagesSkeleton() {
  // Stable widths chosen to look like a normal short-thread tail: a
  // longer prompt, a short reply, a one-liner, a longer reply.
  const rows: Array<{ side: "in" | "out"; w: string }> = [
    { side: "in",  w: "w-48" },
    { side: "out", w: "w-32" },
    { side: "in",  w: "w-24" },
    { side: "out", w: "w-56" },
    { side: "in",  w: "w-40" },
    { side: "out", w: "w-28" },
  ];
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {rows.map((r, i) => (
        <div
          key={i}
          className={cn(
            "flex gap-2",
            r.side === "out" && "flex-row-reverse"
          )}
        >
          {r.side === "in" && <Skeleton className="h-6 w-6 rounded-full shrink-0" />}
          <div className={cn("space-y-1", r.side === "out" && "items-end")}>
            <Skeleton className={cn("h-9 rounded-2xl", r.w)} />
            <Skeleton className="h-2.5 w-16 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function emailHtmlFor(message: Message): string | null {
  const raw = message.raw_payload as { html?: unknown; body?: { html?: unknown } } | undefined;
  const fromRaw = typeof raw?.html === "string" ? raw.html
    : typeof raw?.body?.html === "string" ? raw.body.html
    : null;
  if (fromRaw && fromRaw.trim()) return fromRaw;
  if (message.body && message.body.trim().startsWith("<")) return message.body;
  return null;
}

function MessageBubble({ message, contactName, contactAvatar }: { message: Message; contactName?: string | null; contactAvatar?: string | null }) {
  const isOutbound = message.direction === "outbound";
  const isEmail = message.channel === "email";
  const isInternalNote = message.channel === "internal_note";
  const html = isEmail ? emailHtmlFor(message) : null;
  const [previewOpen, setPreviewOpen] = useState(false);
  // Long emails open in place; the reader window is for the styled version.
  const [expanded, setExpanded] = useState(false);
  const directionLabel = isOutbound ? "Sent via" : "Received via";
  const sender = isEmail ? formatSender(message.from_name, message.from_address) : "";

  if (isInternalNote) {
    return (
      <div className="flex gap-2">
        <div className="w-full max-w-[90%] mx-auto">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
            <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-amber-300">
              <StickyNote className="h-3 w-3" />
              <span>Internal note</span>
              <span className="text-amber-500/50">·</span>
              <span className="normal-case tracking-normal text-amber-300/80">Only your team sees this</span>
            </div>
            <p className="mt-1.5 whitespace-pre-wrap wrap-break-word text-amber-50/90">
              {message.body || <span className="opacity-60 italic">(empty)</span>}
            </p>
          </div>
          <p className="mt-1 text-[10px] text-amber-400/70 px-1">
            {formatRelative(message.sent_at ?? message.created_at)}
          </p>
        </div>
      </div>
    );
  }

  if (isEmail) {
    return (
      <>
        <div className={cn("flex gap-2", isOutbound && "flex-row-reverse")}>
          {!isOutbound && <Avatar name={contactName ?? "?"} src={contactAvatar} size="xs" />}
          <div className={cn("max-w-[85%] space-y-1", isOutbound && "items-end")}>
            <div className={cn(
              "rounded-xl border p-3 text-sm",
              isOutbound ? "border-indigo-500/30 bg-indigo-500/10" : "border-zinc-800 bg-zinc-900/60",
            )}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <Mail className="h-3 w-3" />
                <span>Email</span>
                <span className="text-zinc-600">·</span>
                <span className="capitalize">{message.status}</span>
              </div>
              {/* Exactly which identity this message used. Recorded at send
                  time, so historical messages keep showing the address they
                  actually went out as even after the workspace changes its
                  sender. */}
              {sender && (
                <p className="mt-1 text-[10px] text-zinc-500 truncate" title={sender}>
                  <span className="uppercase tracking-wider">From</span>{" "}
                  <span className="text-zinc-400">{sender}</span>
                </p>
              )}
              {message.subject && (
                <p className="mt-1 text-sm font-semibold text-zinc-100 wrap-break-word">{message.subject}</p>
              )}
              {/* The body was clamped to four lines with no way to lift the
                  clamp, so anything longer ended in "…" and could not be
                  read at all unless it happened to be HTML. It expands in
                  place now, and a plain-text email gets the same reader
                  window an HTML one has. */}
              <p
                data-email-body
                data-expanded={expanded ? "1" : "0"}
                className={cn(
                  "mt-1 text-xs text-zinc-300 whitespace-pre-wrap wrap-break-word",
                  !expanded && "line-clamp-4",
                )}
              >
                {message.body
                  ? (html ? stripHtmlForPreview(message.body) : message.body)
                  : <span className="opacity-60 italic">(empty)</span>}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {(message.body ?? "").length > 180 && (
                  <button
                    type="button"
                    data-email-expand
                    onClick={() => setExpanded((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-300 hover:text-indigo-200"
                  >
                    {expanded ? "Show less" : "Read the whole email"}
                  </button>
                )}
                <button
                  type="button"
                  data-email-open
                  onClick={() => setPreviewOpen(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-indigo-300 hover:text-indigo-200"
                >
                  <Eye className="h-3 w-3" /> {html ? "View styled email" : "Open in a window"}
                </button>
              </div>
            </div>
            <p className={cn("text-[10px] text-zinc-500 px-1 inline-flex items-center gap-1", isOutbound && "justify-end w-full")}>
              {message.status === "queued" && <Loader2 className="inline h-2.5 w-2.5 animate-spin" />}
              <Mail className="h-2.5 w-2.5" />
              <span>{directionLabel} email</span>
              <span className="text-zinc-600">·</span>
              <span>{formatRelative(message.sent_at ?? message.created_at)}</span>
              {message.status === "failed" && <span className="text-rose-400">· {message.error || "failed"}</span>}
            </p>
          </div>
        </div>
        {previewOpen && (
          <EmailPreviewModal
            subject={message.subject}
            html={html ?? plainAsHtml(message.body ?? "")}
            onClose={() => setPreviewOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className={cn("flex gap-2", isOutbound && "flex-row-reverse")}>
      {!isOutbound && <Avatar name={contactName ?? "?"} src={contactAvatar} size="xs" />}
      <div className={cn("max-w-[80%] space-y-0.5", isOutbound && "items-end")}>
        {message.subject && (
          <p className={cn("text-xs font-semibold", isOutbound ? "text-zinc-300 text-right" : "text-zinc-300")}>{message.subject}</p>
        )}
        <MessageAttachments attachments={message.attachments} isOutbound={isOutbound} />
        {(message.body || message.attachments.length === 0) && (
          <div className={cn(
            "rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap wrap-break-word",
            isOutbound ? "bg-indigo-600 text-white" : "bg-zinc-800 text-zinc-200"
          )}>
            {message.body || <span className="opacity-60 italic">(empty)</span>}
          </div>
        )}
        {/* Provenance line: every message shows which channel it went
            through, so a unified thread mixing SMS / email / IG / etc.
            is always readable at a glance. */}
        <p className={cn("text-[10px] text-zinc-500 px-1 inline-flex items-center gap-1 flex-wrap", isOutbound && "justify-end w-full")}>
          {message.status === "queued" && <Loader2 className="inline h-2.5 w-2.5 animate-spin" />}
          <ChannelIcon channel={message.channel} />
          <span>{directionLabel} {channelLabel(message.channel)}</span>
          <span className="text-zinc-600">·</span>
          <span>{formatRelative(message.sent_at ?? message.created_at)}</span>
          {isOutbound && message.status === "delivered" && <span>· Delivered</span>}
          {isOutbound && message.status === "read" && <span className="text-indigo-300">· Read</span>}
          {message.status === "failed" && <span className="text-rose-400">· {message.error || "failed"}</span>}
        </p>
      </div>
    </div>
  );
}

/**
 * Photos and files on a message: texts (MMS) store plain URLs, iMessage and
 * social DMs store { url, name, type }. Images show inline; anything else is
 * a download link. Entries without a usable link are listed by name.
 */
function MessageAttachments({ attachments, isOutbound }: { attachments: unknown[]; isOutbound: boolean }) {
  const items = (attachments ?? [])
    .map((a) => {
      if (typeof a === "string") return { url: a, name: a.split("/").pop() ?? "file", type: null as string | null };
      const o = (a ?? {}) as { url?: unknown; name?: unknown; type?: unknown; mimeType?: unknown };
      return {
        url: typeof o.url === "string" ? o.url : null,
        name: typeof o.name === "string" ? o.name : "file",
        type: typeof o.type === "string" ? o.type : typeof o.mimeType === "string" ? o.mimeType : null,
      };
    })
    .filter((a) => a.url || a.name);
  if (items.length === 0) return null;
  const isImage = (a: { url: string | null; name: string; type: string | null }) =>
    (a.type ?? "").startsWith("image/") || /\.(jpe?g|png|gif|webp|heic)(\?|$)/i.test(a.url ?? a.name);
  return (
    <div className={cn("flex flex-wrap gap-1.5", isOutbound && "justify-end")}>
      {items.map((a, i) =>
        a.url && /^https?:/.test(a.url) && isImage(a) ? (
          <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block">
            <img src={a.url} alt={a.name} loading="lazy" className="max-h-60 max-w-[240px] rounded-xl border border-zinc-800 object-cover" />
          </a>
        ) : a.url && /^https?:/.test(a.url) ? (
          <a key={i} href={a.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-800">
            📎 <span className="max-w-[200px] truncate">{a.name}</span>
          </a>
        ) : (
          <span key={i} className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-400">
            📎 <span className="max-w-[200px] truncate">{a.name}</span>
          </span>
        ),
      )}
    </div>
  );
}

/** A plain-text email, wrapped just enough to open in the reader window. */
function plainAsHtml(body: string): string {
  const escaped = body
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<!doctype html><meta charset="utf-8"><body style="margin:0;padding:24px;font:14px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;color:#18181b;white-space:pre-wrap;word-break:break-word">${escaped}</body>`;
}

function stripHtmlForPreview(html: string): string {
  const stripped = html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>(?=)/gi, "\n")
    .replace(/<\/(p|div|li|h\d)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return stripped;
}

function EmailPreviewModal({ subject, html, onClose }: { subject: string | null; html: string; onClose: () => void }) {
  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-zinc-400" />
          <span className="truncate">{subject ?? "(no subject)"}</span>
        </span>
      }
      size="xl"
    >
      <iframe
        title={subject ?? "Email preview"}
        srcDoc={html}
        sandbox=""
        className="-mx-5 -my-4 h-[75vh] w-[calc(100%+2.5rem)] bg-white"
      />
    </Modal>
  );
}

/**
 * The line under the message box when someone is writing a text.
 *
 * It measures what will ACTUALLY be sent. The send path straightens pasted
 * punctuation first — a curly apostrophe forces an encoding where a text
 * holds 70 characters instead of 160, and nobody can tell the two apart in a
 * message — so counting the raw box would report a cost she is never charged.
 * Nothing to click: the only thing worth attention is a message that is
 * genuinely too long even after that.
 */
function SmsMeter({ body }: { body: string }) {
  const len = smsLength(tidyForSms(body));
  const tone = len.tooLong ? "text-rose-400"
    : len.parts >= MAX_SMS_PARTS - 2 ? "text-amber-400"
    : "text-zinc-500";
  const shorten = Math.ceil(((len.parts - MAX_SMS_PARTS) * (len.encoding === "UCS-2" ? 67 : 153)) / 10) * 10;

  return (
    <div data-sms-meter className="mt-1.5 flex flex-wrap items-center gap-x-2 text-[10px]">
      <span className={tone} data-sms-parts={len.parts}>
        {len.characters.toLocaleString()} characters · {len.parts} of {MAX_SMS_PARTS} text{len.parts === 1 ? "" : "s"}
      </span>
      {len.tooLong && (
        <span className="text-rose-400">
          · too long to send{len.encoding === "UCS-2" && len.forcedBy.length > 0
            ? ` — ${describeForced(len.forcedBy)} halves what fits in a text`
            : ` — shorten it by about ${shorten} characters, or send it as an email`}
        </span>
      )}
    </div>
  );
}
